/**
 * Represents information about a city.
 */
export type cityInfo = {
  /**
   * The name of the city.
   */
  CityName: string;
  /**
   * The code of the city.
   */
  CityCode: string;
  /**
   * The English name of the city.
   */
  CityNameEng: string;
  /**
   * The ID of the city.
   */
  CityID: string;
  /**
   * The code of the county.
   */
  CountyCode: string;
}

export class TDXCityInfo {
    private static cities: cityInfo[] = [
        { CityName: '臺北市', CityCode: 'TPE', CityNameEng: 'Taipei', CityID: 'A', CountyCode: '63000' },
        { CityName: '新北市', CityCode: 'NWT', CityNameEng: 'NewTaipei', CityID: 'F', CountyCode: '65000' },
        { CityName: '桃園市', CityCode: 'TAO', CityNameEng: 'Taoyuan', CityID: 'H', CountyCode: '68000' },
        { CityName: '臺中市', CityCode: 'TXG', CityNameEng: 'Taichung', CityID: 'B', CountyCode: '66000' },
        { CityName: '臺南市', CityCode: 'TNN', CityNameEng: 'Tainan', CityID: 'D', CountyCode: '67000' },
        { CityName: '高雄市', CityCode: 'KHH', CityNameEng: 'Kaohsiung', CityID: 'E', CountyCode: '64000' },
        { CityName: '基隆市', CityCode: 'KEE', CityNameEng: 'Keelung', CityID: 'C', CountyCode: '10017' },
        { CityName: '新竹市', CityCode: 'HSZ', CityNameEng: 'Hsinchu', CityID: 'J', CountyCode: '10004' },
        { CityName: '新竹縣', CityCode: 'HSQ', CityNameEng: 'HsinchuCounty', CityID: 'I', CountyCode: '10020' },
        { CityName: '苗栗縣', CityCode: 'MIA', CityNameEng: 'MiaoliCounty', CityID: 'K', CountyCode: '10005' },
        { CityName: '彰化縣', CityCode: 'CHA', CityNameEng: 'ChanghuaCounty', CityID: 'N', CountyCode: '10007' },
        { CityName: '南投縣', CityCode: 'NAN', CityNameEng: 'NantouCounty', CityID: 'M', CountyCode: '10008' },
        { CityName: '嘉義市', CityCode: 'CYI', CityNameEng: 'Chiayi', CityID: 'G', CountyCode: '10002' },
        { CityName: '嘉義縣', CityCode: 'CYQ', CityNameEng: 'ChiayiCounty', CityID: 'Q', CountyCode: '10010' },
        { CityName: '雲林縣', CityCode: 'YUN', CityNameEng: 'YunlinCounty', CityID: 'P', CountyCode: '10009' },
        { CityName: '屏東縣', CityCode: 'PIF', CityNameEng: 'PingtungCounty', CityID: 'T', CountyCode: '10013' },
        { CityName: '宜蘭縣', CityCode: 'ILA', CityNameEng: 'YilanCounty', CityID: 'G', CountyCode: '10002' },
        { CityName: '花蓮縣', CityCode: 'HUA', CityNameEng: 'HualienCounty', CityID: 'U', CountyCode: '10015' },
        { CityName: '臺東縣', CityCode: 'TTT', CityNameEng: 'TaitungCounty', CityID: 'V', CountyCode: '10014' },
        { CityName: '澎湖縣', CityCode: 'PEN', CityNameEng: 'PenghuCounty', CityID: 'X', CountyCode: '10016' },
        { CityName: '連江縣', CityCode: 'LIE', CityNameEng: 'LienchiangCounty', CityID: 'Z', CountyCode: '9007' },
        { CityName: '金門縣', CityCode: 'KIN', CityNameEng: 'KinmenCounty', CityID: 'W', CountyCode: '9020' }
    ];

    /**
     * 根據城市名稱取得城市資訊
     *
     * @static
     * @param {string} cityName 城市名稱, 例如: '臺北市'
     * @returns {(cityInfo | undefined)}
     */
    static getCityInfoByCityName(cityName: string): cityInfo | undefined {
        return this.cities.find(city => city.CityName.toLocaleLowerCase() === cityName.toLocaleLowerCase());
    }
    /**
     * 根據城市代碼取得城市資訊
     *
     * @static
     * @param {string} cityCode 城市代碼, 例如: 'TPE'
     * @returns {(cityInfo | undefined)}
     */
    static getCityInfoByCityCode(cityCode: string): cityInfo | undefined {
      return this.cities.find(city => city.CityCode.toLocaleLowerCase() === cityCode.toLocaleLowerCase());
    }

    /**
     * 根據城市英文名稱取得城市資訊
     *
     * @static
     * @param {string} cityNameEng 城市英文名稱, 例如: 'Taipei'
     * @returns {(cityInfo | undefined)}
     */
    static getCityInfoByCityNameEng(cityNameEng: string): cityInfo | undefined {
      return this.cities.find(city => city.CityNameEng.toLocaleLowerCase() === cityNameEng.toLocaleLowerCase());
    }

    /**
     * 根據城市ID取得城市資訊
     *
     * @static
     * @param {string} cityID 城市ID, 例如: 'A'
     * @returns {(cityInfo | undefined)}
     */
    static getCityInfoByCityID(cityID: string): cityInfo | undefined {
      return this.cities.find(city => city.CityID.toLocaleLowerCase() === cityID.toLocaleLowerCase());
    }

    /**
     * 根據縣市代碼取得城市資訊
     *
     * @static
     * @param {string} countyCode 縣市代碼, 例如: '63000'
     * @returns {(cityInfo | undefined)}
     */
    static getCityInfoByCountyCode(countyCode: string): cityInfo | undefined {
      return this.cities.find(city => city.CountyCode.toLocaleLowerCase() === countyCode.toLocaleLowerCase());
    }

    /**
     * 取得所有城市資訊
     *
     * @static
     * @returns {cityInfo[]}
     */
    static getAllCityInfo(): cityInfo[] {
      return this.cities;
    }
    
}